import { toast } from 'sonner'
import { whatWentWrong } from './whatWentWrong.js'

/**
 * A write with no form around it, and what happens when it is refused.
 *
 * Most writes on this desk live in a panel that can draw a banner. These are
 * the other kind: a button on a row, an item in a ⋯ menu, an arrow that moves
 * something up. They were written as `void remove(...)` — fire, and never
 * look — so a refusal was indistinguishable from a success. The row stayed
 * where it was, nothing was said, and the person turned back to their work
 * believing the thing was done.
 *
 * The worst of them were in a live class: a host presses Remove on something
 * offensive in front of a room of people, the request fails, the comment
 * stays on everybody's screen and the desk says nothing.
 *
 * Nothing is said on success, deliberately. The row changing — struck
 * through, moved, gone — is the confirmation, and a toast for every press is
 * noise on a screen somebody is pressing things on quickly.
 *
 * The sentence is the server's where there is one. See `whatWentWrong`.
 */
export function act(
  /**
   * The write. An RTK mutation (which has to be unwrapped before it rejects)
   * or a plain promise, for the few places that chain two of them.
   */
  run: { unwrap: () => Promise<unknown> } | Promise<unknown>,
  failed: string,
): void {
  const settled = 'unwrap' in run ? run.unwrap() : run
  void settled.catch((e: unknown) => toast.error(whatWentWrong(e, failed)))
}

/**
 * The one write that must not say whether it worked.
 *
 * "Send it again" on the reset-password screen. The banner above it is
 * deliberately the same sentence either way — *"if that address has an
 * account, a link is on its way"* — because an answer that varied would be a
 * way of finding out who has an account here. Reporting the failure would
 * undo that in one line.
 *
 * So the refusal is swallowed, on purpose and by name, rather than by an
 * anonymous `.catch(() => {})` that reads as an oversight. The standing
 * assertion in `what-it-says-when-it-fails` allows this and nothing else.
 */
export function saidNothingOnPurpose(run: { unwrap: () => Promise<unknown> }): void {
  void run.unwrap().catch(() => {})
}
